import { Avatar, AvatarGroup, ButtonBase, Stack, SxProps, Theme } from '@mui/material';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { selectExpertEntities } from 'FRONTEND/reducers/expertSlice';
import { FormattedParticipant } from 'FRONTEND/utils/backendApi/Appointments/appointment.types';
import { Expert } from 'FRONTEND/utils/backendApi/expert/expert.types';

type InitialsAvatarProps = {
  picture?: string;
  initials: string;
  color?: string;
  sideSize?: number;
  onClick?: () => void;
  additionalUsers?: Record<string, FormattedParticipant>; // object with userIds as keys, value is not used
  sx?: SxProps<Theme>; // MUI sx prop for custom styling
}

const InitialsAvatar = ({
  picture,
  initials,
  color,
  sideSize = 20,
  onClick,
  additionalUsers = {},
  sx,
  ...otherProps
}: InitialsAvatarProps) => {
  // check expertEntities for available images
  const expertEntities = useSelector(selectExpertEntities);
  const multiUserAvatarArray = useMemo(() => {
    if (!additionalUsers) {
      return [];
    }

    return Object.keys(additionalUsers).map((userId) => {
      const user: Expert = expertEntities[userId] as Expert;
      return (
        <Avatar
          key={user?.id}
          sx={{ height: `${sideSize}px`, width: `${sideSize}px`, fontSize: '0.75rem' }}
          src={user?.picture}
        />
      );
    });
  }, [additionalUsers, expertEntities]);

  return (
    <Stack>
      {picture ? (
        <ButtonBase onClick={onClick} disabled={!onClick}>
          <AvatarGroup max={2}>
            <Avatar
              src={picture}
              alt={initials}
              sx={[
                {
                  height: `${sideSize}px`,
                  width: `${sideSize}px`,
                  fontSize: '0.75rem',
                  zIndex: 10,
                },
                ...(Array.isArray(sx) ? sx : [sx]), // https://mui.com/system/getting-started/the-sx-prop/#passing-the-sx-prop
              ]}
              {...otherProps}
            />
            {multiUserAvatarArray}
          </AvatarGroup>
        </ButtonBase>
      ) : (
        <ButtonBase onClick={onClick} disabled={!onClick}>
          <AvatarGroup max={2}>
            <Avatar
              style={{ backgroundColor: color }}
              sx={{
                height: `${sideSize}px`,
                width: `${sideSize}px`,
                fontSize: '0.75rem',
              }}
            >
              {initials}
            </Avatar>
            {multiUserAvatarArray}
          </AvatarGroup>
        </ButtonBase>
      )}
    </Stack>
  );
};

export default React.memo(InitialsAvatar);
